'use client';
import { AuthViews } from '@repo/ui/src/modules/UI/constants';
import LanguageSelector from '../LanguageSelector';
import MobileSecondaryNav from './_MobileSecondaryNav';
import NavDropdown, { NavDropdownProps } from './_NavDropdown';
import { NavItemProps } from './index';
import { Anchor } from '@repo/ui/src/components/Anchor';
import AccountIcon from '@repo/ui/src/components/Icons/AccountIcon';
import Image from '@repo/ui/src/components/Image';
import usePrevious from '@repo/ui/src/hooks/usePrevious';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { LinkProps } from '@repo/ui/src/types';
import Accordion, { AccordionItem } from '@repo/ui/src/components/Accordion';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import { classNames } from '@repo/ui/src/utils/classNames';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import FocusTrap from 'focus-trap-react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';
import { Region } from '@repo/ui/src/types/LanguageSelector';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import { appConfig } from '../../utils/appConfig';

export interface MobileNavProps {
  open: boolean;
  offset: number;
  navItems: Array<NavItemProps>;
  regions: Array<Region>;
  searchLink: LinkProps;
  dealerLocator?: LinkProps;
  account?: {
    greeting?: string;
    loggedInLabel?: string;
    loggedOutText: string;
    link?: LinkProps;
  };
  // Hooks passed as props for flexibility
  useCustomer?: () => { data: any };
  useLogout?: () => { trigger: () => void };
}

const componentTypes = {
  'secondaryNav.json': MobileSecondaryNav,
  'navDropdown.json': NavDropdown,
};

const MobileNav = ({
  open,
  offset,
  navItems,
  searchLink,
  regions,
  account,
  dealerLocator,
  useCustomer,
  useLogout,
}: MobileNavProps) => {
  const { t } = useTranslation();
  const { data: customer } = useCustomer?.() || { data: null };
  const { trigger: logout } = useLogout?.() || { trigger: () => {} };
  const [activeDropdown, setActiveDropdown] = React.useState<NavDropdownProps | null>(null);
  const [activeNav, setActiveNav] = React.useState<NavItemProps | null>(null);
  const [visible, setVisible] = React.useState(open);
  const previousDropdown = usePrevious(activeDropdown);
  const { push } = useModal(ModalID.login);

  React.useEffect(() => {
    setVisible(true);
    if (!open) {
      setActiveDropdown(null);
    }
  }, [open]);

  return (
    <nav
      data-testid="mobile-nav"
      aria-label="mobile"
      className={classNames(
        'tw-fixed tw-inset-0 sm:tw-hidden tw-row-start-2 tw-transition-transform tw-duration-500 -tw-z-10 tw-max-h-screen tw-flex tw-flex-col',
        appConfig.isTraeger ? 'tw-bg-warm-100' : 'tw-bg-neutral-100',
        open ? 'tw-translate-y-0' : '-tw-translate-y-full tw-pointer-events-none',
        !visible && 'tw-invisible',
      )}
      onTransitionEnd={() => {
        if (!open) {
          setVisible(false);
        }
      }}
      style={{ paddingTop: `${offset}px` }}
    >
      <section
        className={classNames(
          'tw-grid tw-grid-cols-2 tw-w-[200%] tw-overflow-x-hidden tw-transition-transform tw-duration-500 tw-h-full tw-overflow-y-auto',
          activeDropdown && '-tw-translate-x-1/2',
        )}
      >
        <FocusTrap
          active={open && visible && activeDropdown == null}
          focusTrapOptions={{
            allowOutsideClick: true,
          }}
        >
          <div className="tw-max-h-full tw-overflow-y-auto tw-px-6">
            <Accordion allowZeroExpanded>
              {navItems.map((item, i) => {
                const hasContent = !!item.content;

                if (!hasContent) return null;

                return (
                  <AccordionItem
                    heading={item.title}
                    key={i}
                    uuid={i}
                    headerFontClass="tra-text-web-heading-4"
                    className="!tw-my-0"
                  >
                    <DynamicComponent
                      {...item.content}
                      _componentTypes={componentTypes}
                      searchLink={searchLink}
                      setActiveDropdown={(value: NavDropdownProps | null) => {
                        setActiveDropdown(value);
                        setActiveNav(item);
                      }}
                      activeDropdown={activeDropdown}
                      primaryHeader={item?.title}
                    />
                  </AccordionItem>
                );
              })}
            </Accordion>

            {navItems.map((item, i) => {
              const hasLink = !!item.titleAsLink;
              const hasContent = !!item.content;

              if (!hasLink || hasContent) return null;

              return (
                <div key={`link-${i}`} className="tw-py-6 tw-border-b tw-border-neutral-300 last:tw-border-b-0">
                  <Anchor
                    {...item.titleAsLink}
                    className="tra-text-web-heading-4 tw-text-neutral-700"
                    onClick={() =>
                      navigationClicked({
                        nav_type: 'Top Navigation',
                        clicked_text: item.title ?? '',
                        primary_header: item.title ?? '',
                      })
                    }
                  >
                    {item.title}
                  </Anchor>
                </div>
              );
            })}
            {dealerLocator && (
              <div className="tw-py-6">
                <Anchor
                  {...dealerLocator}
                  className="tra-text-web-heading-4 tw-text-neutral-700"
                  onClick={() =>
                    navigationClicked({
                      nav_type: 'Top Navigation',
                      clicked_text: dealerLocator.displayName ?? '',
                      primary_header: dealerLocator.displayName ?? '',
                    })
                  }
                />
              </div>
            )}
          </div>
        </FocusTrap>
        <FocusTrap
          active={activeDropdown != null}
          focusTrapOptions={{
            allowOutsideClick: true,
          }}
        >
          <div className="tw-px-2" id="mobile-tertiary-nav">
            {(activeDropdown || previousDropdown) && (
              <NavDropdown
                {...((activeDropdown ?? previousDropdown) as NavDropdownProps)}
                setActiveDropdown={setActiveDropdown}
                showMobileHeader
                primaryHeader={activeNav?.title}
                secondaryHeader={activeDropdown?.title}
              />
            )}
          </div>
        </FocusTrap>
      </section>
      <section className="tw-bg-warm-200 tw-py-5 tw-px-6">
        <div className="tw-grid tw-grid-cols-[auto_auto_1fr] tw-gap-x-4 tw-text-neutral-500 tw-items-center">
          {appConfig.isTraeger &&
            <>
              {customer ? (
                <div className={classNames('tw-relative tw-w-6 tw-h-6')}>
                  <Image
                    className="tw-rounded-full"
                    src={customer?.avatar ?? '/icons/account.svg'}
                    alt=""
                    role="presentation"
                    layout="fill"
                  />
                </div>
              ) : (
                <AccountIcon className="tw-w-6" />
              )}
              <div className="tw-flex tw-gap-x-2 tw-items-center">
                {customer ? (
                  <>
                    <Button
                      as={Anchor}
                      aria-label={account?.loggedOutText}
                      label={account?.loggedInLabel}
                      link={t('accountLink.link', undefined, { default: '/account/order-history' })}
                      variant={ButtonVariant.bare}
                      size="none"
                      dark
                      className="tw-capitalize"
                    >
                      {account?.link?.displayName ?? 'View account'}
                    </Button>
                    |
                    <Button
                      aria-label={account?.loggedOutText}
                      variant={ButtonVariant.bare}
                      size="none"
                      dark
                      onClick={() => logout()}
                      className="tw-capitalize"
                    >
                      {t('account.logoutText', undefined, { default: 'Logout' })}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      aria-label={account?.loggedOutText}
                      variant={ButtonVariant.bare}
                      size="none"
                      dark
                      onClick={() => push()}
                      className="tw-capitalize"
                    >
                      {t('account.loginText', undefined, { default: 'Log In' })}
                    </Button>
                    |
                    <Button
                      aria-label={account?.loggedOutText}
                      variant={ButtonVariant.bare}
                      size="none"
                      dark
                      onClick={() => push({ view: AuthViews.CREATE })}
                      className="tw-capitalize"
                    >
                      {t('account.createAccountText', undefined, { default: 'Create Account' })}
                    </Button>
                  </>
                )}
              </div>
            </>
          }
          <div className="tw-justify-self-end">
            <LanguageSelector regions={regions} />
          </div>
        </div>
      </section>
    </nav>
  );
};

export default MobileNav;
